import { useMemo } from 'react';
import orderBy from 'lodash/orderBy';

import { Eaiv_Action_Type, Eaiv_Result, useEaivAuditTrailQuery } from '@occc/graphql';

import { IEaivMembershipStatistics } from './types';
import {
  getDefaultEaivMembershipStatistics,
  EAIV_ERROR_REASON_DEFAULT_MESSAGE,
  EAIV_ERROR_REASON_TIMESTAMP_FORMAT,
} from './contants';

import { formatDateTime, parseDate } from '../../lib/date';

export const useEaivAuditTrail = (params: { customerUuid: string }) => {
  const { data, loading } = useEaivAuditTrailQuery({
    variables: { customerUuid: params.customerUuid },
    // prevent query on empty string
    skip: !params.customerUuid,
  });

  const nodes = useMemo(() => data?.eaivAuditTrail.edges.map((edge) => edge.node) ?? null, [data]);
  return { data: nodes, loading };
};

export const useEaivStatistics = (params: { customerUuid: string }) => {
  const { data, loading } = useEaivAuditTrail({ customerUuid: params.customerUuid });
  const statistics = useMemo<IEaivMembershipStatistics>(
    () =>
      orderBy(data, ['createdAt'], ['desc']).reduce<IEaivMembershipStatistics>((acc, eaivAudit) => {
        if (
          eaivAudit.actionType === Eaiv_Action_Type.AgeVerificationStart &&
          eaivAudit.result === Eaiv_Result.Success
        ) {
          acc.verificationAttempts.ageSubmitSuccess += 1;
        }

        if (
          eaivAudit.actionType === Eaiv_Action_Type.AgeVerificationStart &&
          eaivAudit.result === Eaiv_Result.Failure
        ) {
          acc.verificationAttempts.ageSubmitFailed += 1;
          acc.verificationAttempts.ageSubmitErrorReasons.push({
            createdAt: formatDateTime(
              parseDate(eaivAudit.createdAt),
              EAIV_ERROR_REASON_TIMESTAMP_FORMAT,
            ),
            message: eaivAudit.reasonMessage || EAIV_ERROR_REASON_DEFAULT_MESSAGE,
          });
        }

        if (
          eaivAudit.actionType === Eaiv_Action_Type.IdentityVerificationStart &&
          eaivAudit.result === Eaiv_Result.Success
        ) {
          acc.verificationAttempts.identityStartSuccess += 1;
        }

        if (
          eaivAudit.actionType === Eaiv_Action_Type.IdentityVerificationStart &&
          eaivAudit.result === Eaiv_Result.Failure
        ) {
          acc.verificationAttempts.identityStartFailed += 1;
          acc.verificationAttempts.identityStartErrorReasons.push({
            createdAt: formatDateTime(
              parseDate(eaivAudit.createdAt),
              EAIV_ERROR_REASON_TIMESTAMP_FORMAT,
            ),
            message: eaivAudit.reasonMessage || EAIV_ERROR_REASON_DEFAULT_MESSAGE,
          });
        }

        if (
          eaivAudit.actionType === Eaiv_Action_Type.IdentityVerificationSubmit &&
          eaivAudit.result === Eaiv_Result.Success
        ) {
          acc.verificationAttempts.identitySubmitSuccess += 1;
        }

        if (
          eaivAudit.actionType === Eaiv_Action_Type.IdentityVerificationSubmit &&
          eaivAudit.result === Eaiv_Result.Failure
        ) {
          acc.verificationAttempts.identitySubmitFailed += 1;
          acc.verificationAttempts.identitySubmitErrorReasons.push({
            createdAt: formatDateTime(
              parseDate(eaivAudit.createdAt),
              EAIV_ERROR_REASON_TIMESTAMP_FORMAT,
            ),
            message: eaivAudit.reasonMessage || EAIV_ERROR_REASON_DEFAULT_MESSAGE,
          });
        }

        return acc;
      }, getDefaultEaivMembershipStatistics()) ?? getDefaultEaivMembershipStatistics(),
    [data],
  );

  return { loading, statistics };
};
